// src/components/partial-pages/MainPage.tsx
import { memo, useEffect, useState, useCallback } from 'react'
import { Route, Switch, useLocation } from 'react-router-dom'
import AlertsView from 'components/partial-pages/AlertsView/AlertsView'
import BeaconsPage from 'components/partial-pages/BeaconsPage/BeaconsPage'
import { DevicesPage } from 'components/partial-pages/ButtonsDevicesPage'
import MainDashboardPage from 'components/partials/_legacy/MainDashboardPage/MainDashboardPage'
import UsersPage from 'components/partial-pages/UsersPage/UsersPage'
import { RespondersPage } from 'components/partial-pages/RespondersPage/RespondersPage'

import GeneralSettingsPage from 'components/partial-pages/GeneralSettingsPage/GeneralSettingsPage'
import { MainHeader, StaticModal } from 'components/partials'
import { MainSidebar, DetailSlider } from 'components/partials'
import {
  DASHBOARD_MAIN_PATH,
  GENERAL_SETTINGS_PATH,
  PATH_ADMIN_ALL,
  PATH_ALERTS,
  PATH_DEVICES,
  PATH_RESPONDERS,
  PATH_BEACONS,
  PATH_USERS,
  PATH_GATEWAYS,
  PATH_CHANNELS,
  BRAND_COLOR,
  ALL_ORGS_SELECTED,
} from 'lib/constants'
import UseDesktopNotifications from '../legacy-poorly-defined/useDesktopNotifications'
import { reloadAll } from 'models'
import { PortalRequired } from 'components/partials/PortalRequired'
import { AdminView } from 'components/partial-pages/AdminView'
import { scrollToTop } from 'lib/utils'
import { useIdleTimer } from 'react-idle-timer'
import { RenderCounter } from 'components/widgets/RenderCounter'
import { CenteredLoader, PageWrapper, ViewWrapper } from 'components/widgets'
import AudioContextOverlay from 'components/widgets/AudioContextOverlay'

import { BeaconsProvider } from 'contexts/BeaconsProvider'
import { DevicesProvider } from 'contexts/DevicesProvider'
import { useGlobal } from 'contexts/GlobalProvider'
import { AlertsProvider } from 'contexts/AlertsProvider'
import { RespondersProvider } from 'contexts/RespondersProvider'
import { AdminProvider } from 'contexts/AdminProvider'
import { isOnlyInstaller, isVoiceEnabled } from 'lib/utils/auth'
import NotAuthorizedPage from './NotAuthorizedPage'
import OrgSuspendedPage from './OrgSuspendedPage'
import { Box, Button } from '@material-ui/core'
import GatewaysPage from 'components/partial-pages/GatewaysPage/GatewaysPage'
import { GatewaysProvider } from 'contexts/GatewaysProvider'
import { ChannelsProvider } from 'contexts/ChannelsProvider'

import { queryMicPermission, type MicPermissionState } from 'lib/audio/permissions'
import VoicePage from 'components/partial-pages/VoicePage/VoicePage'
import { unlockPcmAudio } from 'lib/audio/OpusStreamPlayer'

type MicPerm = MicPermissionState | null

const MainPage = () => {
  if ('Notification' in window) {
    Notification.requestPermission().then(result => {
      console.log('Notification Permission:', result)
    })
  } else {
    console.log('This browser does not support notifications.')
  }

  const { pathname } = useLocation()
  const { selectedOrgId, selectedOrg, currentUser, orgs, loading } = useGlobal()
  const [section, setSection] = useState('')
  const [micPermission, setMicPermission] = useState<MicPerm>(null)
  const [micError, setMicError] = useState<string | null>(null)

  const stopAllTracks = useCallback((st?: MediaStream) => {
    st?.getTracks?.().forEach(t => {
      try {
        t.stop()
      } catch (err) {
        void err
      }
    })
  }, [])

  const requestMicByGesture = useCallback(async () => {
    setMicError(null)
    void unlockPcmAudio()
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        setMicPermission('unsupported')
        setMicError('MediaDevices.getUserMedia is not available in this browser.')
        return
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true },
      })
      stopAllTracks(stream)

      setMicPermission('granted')
    } catch (e) {
      const msg = (e as any)?.message ?? String(e)
      setMicPermission('denied')
      setMicError(msg)
    }
  }, [stopAllTracks])

  useEffect(() => {
    if (!selectedOrg || !isVoiceEnabled(selectedOrg)) {
      setMicPermission(null)
      setMicError(null)
      return
    }

    let active = true
    ;(async () => {
      const state = await queryMicPermission()
      if (!active) return
      setMicPermission(state)
    })()

    return () => {
      active = false
    }
  }, [selectedOrg])

  useIdleTimer({
    timeout: 1000 * 60 * 60,
    crossTab: true,
    syncTimers: 200,
    onIdle: () => {
      reloadAll()
    },
  })

  const getSection = () => {
    const parts = pathname.split('/')
    return parts.length > 1 ? parts[2] : parts[parts.length - 1]
  }

  useEffect(() => {
    reloadAll()
    console.log('is only installer:', isOnlyInstaller(selectedOrgId))
    console.log('current user:', currentUser)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const newSection = getSection()
    if (newSection !== section) {
      setSection(newSection)
      scrollToTop()
    }
  }, [pathname]) // eslint-disable-line react-hooks/exhaustive-deps

  const showMicBanner = micPermission === 'prompt' || micPermission === 'unsupported' || micPermission === 'denied'
  const showDelinquentBanner = selectedOrg?.subscriptionStatus === 'delinquent'
  const showOrgSuspendedPage =
    selectedOrg?.subscriptionStatus === 'suspended' && orgs.length > 1 && !pathname.includes('/admin')

  return (
    <PortalRequired>
      <ChannelsProvider>
        <>
          <RenderCounter name={'MainPage'} />
          <MainHeader />
          <AudioContextOverlay />
          <UseDesktopNotifications />

          {showDelinquentBanner && (
            <Box
              role="region"
              aria-label="Past due payment notice"
              style={{
                background: '#FFE8E6',
                color: '#7A1E00',
                border: '1px solid #FFB3A7',
                borderRadius: 8,
                padding: '10px 12px',
                margin: '8px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <div style={{ fontSize: 14, lineHeight: 1.3 }}>
                <b>Action Required: Payment Past Due</b> – Please submit payment as soon as possible to avoid service
                interruption. For questions, contact Client Success at client.success@reactmobile.com.
              </div>
            </Box>
          )}

          {showMicBanner && (
            <Box
              role="region"
              aria-label="Microphone permission"
              style={{
                background: micPermission === 'denied' ? '#FFE8E6' : '#FFF4E5',
                color: micPermission === 'denied' ? '#7A1E00' : '#7A4E00',
                border: `1px solid ${micPermission === 'denied' ? '#FFB3A7' : '#FFD7A8'}`,
                borderRadius: 8,
                padding: '10px 12px',
                margin: '8px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <div style={{ fontSize: 14, lineHeight: 1.3 }}>
                {micPermission === 'denied' && (
                  <>
                    Microphone access is <b>blocked</b> for this site. Push-to-talk won’t work until you allow the mic
                    in your browser settings. {micError ? `(${micError})` : ''}
                  </>
                )}
                {micPermission === 'prompt' && <>To use push-to-talk, please enable your microphone.</>}
                {micPermission === 'unsupported' && (
                  <>Your browser didn’t report mic permission status. Click below to enable the microphone.</>
                )}
              </div>
              {micPermission !== 'denied' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Button
                    variant="contained"
                    onClick={requestMicByGesture}
                    style={{
                      backgroundColor: BRAND_COLOR,
                      color: '#fff',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Enable Microphone
                  </Button>
                </div>
              )}
            </Box>
          )}

          <MainSidebar />
          {showOrgSuspendedPage ? (
            <OrgSuspendedPage />
          ) : isOnlyInstaller(selectedOrgId) ? (
            <Box display="flex" marginLeft={'15%'}>
              <NotAuthorizedPage />
            </Box>
          ) : (
            <>
              <Switch key={selectedOrgId}>
                <Route exact path={DASHBOARD_MAIN_PATH}>
                  <MainDashboardPage />
                </Route>
                <Route exact path={PATH_BEACONS}>
                  <BeaconsProvider>
                    <BeaconsPage />
                  </BeaconsProvider>
                </Route>
                <Route exact path={PATH_DEVICES}>
                  <DevicesProvider>
                    <DevicesPage />
                  </DevicesProvider>
                </Route>
                <Route exact path={PATH_RESPONDERS}>
                  <RespondersProvider>
                    <RespondersPage />
                  </RespondersProvider>
                </Route>
                <Route exact path={PATH_USERS}>
                  <UsersPage />
                </Route>
                <Route exact path={PATH_ALERTS}>
                  <AlertsProvider>
                    <AlertsView />
                  </AlertsProvider>
                </Route>
                <Route exact path={PATH_GATEWAYS}>
                  <GatewaysProvider>
                    <GatewaysPage />
                  </GatewaysProvider>
                </Route>
                <Route exact path={PATH_CHANNELS}>
                  <VoicePage />
                </Route>
                <Route exact path={PATH_ADMIN_ALL}>
                  <AdminProvider>
                    <AdminView />
                  </AdminProvider>
                </Route>
                <Route exact path={GENERAL_SETTINGS_PATH}>
                  {selectedOrg ? (
                    <GeneralSettingsPage />
                  ) : loading || (!!selectedOrgId && selectedOrgId !== ALL_ORGS_SELECTED) ? (
                    // Still resolving the org (e.g. on refresh, before it hydrates
                    // from storage) — show a loader, not the "select an org" message.
                    <PageWrapper>
                      <ViewWrapper>
                        <CenteredLoader />
                      </ViewWrapper>
                    </PageWrapper>
                  ) : (
                    <PageWrapper>
                      <ViewWrapper>
                        <p>Please select an organization</p>
                      </ViewWrapper>
                    </PageWrapper>
                  )}
                </Route>
              </Switch>
            </>
          )}

          <DetailSlider />
          <StaticModal />
        </>
      </ChannelsProvider>
    </PortalRequired>
  )
}

export default memo(MainPage)
